import { ComponentType, LazyExoticComponent, lazy } from 'react';

const LEAVING_FOR_MS = 1_000;
/** Enough for a slow navigation - a Keycloak logout - to finish, retrying once a second. */
const RETRIES_WHILE_LEAVING = 5;

let leavingUntil = 0;

/**
 * WebKit cancels a page's in-flight chunk loads as soon as the page starts leaving, and any it
 * starts while the navigation is still pending, so their imports reject. That is not out-of-date
 * code, and reloading would replace the navigation - a forced logout's included - with a reload of
 * the page being left. `beforeunload` fires before the imports reject.
 */
window.addEventListener('beforeunload', () => {
  leavingUntil = Date.now() + LEAVING_FOR_MS;
});

/**
 * A function to retry loading a chunk to avoid chunk load error for out of date code
 * @param componentImport
 * @param name
 * @returns
 */

export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  name = 'lazy'
  // Return type compatible with react-router v5's component prop
): LazyExoticComponent<T> & ComponentType<unknown> {
  return lazy(() => lazyRetry(componentImport, name)) as LazyExoticComponent<T> & ComponentType<unknown>;
}

function lazyRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  name = 'lazy'
): Promise<{ default: T }> {
  return new Promise((resolve, reject) => {
    const hasRefreshed = JSON.parse(window.sessionStorage.getItem(`retry-${name}-refreshed`) || 'false');
    let retriesWhileLeaving = 0;

    const attempt = () =>
      componentImport()
        .then((component) => {
          window.sessionStorage.setItem(`retry-${name}-refreshed`, 'false');
          resolve(component);
        })
        .catch((error) => {
          // The page is leaving: try again until it has gone, which stops this running. If it
          // stayed - a navigation to a download or a 204 - the chunk loads on the next try.
          const isLeaving = retriesWhileLeaving > 0 || Date.now() < leavingUntil;
          if (isLeaving && retriesWhileLeaving < RETRIES_WHILE_LEAVING) {
            retriesWhileLeaving += 1;
            setTimeout(() => void attempt(), LEAVING_FOR_MS);
            return;
          }
          if (!hasRefreshed) {
            window.sessionStorage.setItem(`retry-${name}-refreshed`, 'true');
            window.location.reload();
            return;
          }
          reject(error);
        });

    attempt();
  });
}
